import _ from 'lodash';

import React, {
    useCallback,
    useContext,
    useState
} from 'react';

import d3 from 'd3';

import {
    createPortal
} from 'react-dom';

import PropTypes from 'prop-types';

import {
    BarChart,
    Bar as BarRecharts,
    CartesianGrid,
    ResponsiveContainer,
    XAxis,
    YAxis
} from 'recharts';

import i18n from '../../../i18n.js';

import ExportContext from '../../contexts/exportContext.js';

import {
    useRIQTooltip
} from '../RIQPopover.jsx';

import {
    ChartTooltip
} from '../ChartTooltip.jsx';

import XAxisTick from '../XAxisTick.jsx';

const {
    t
} = i18n('components/charts/Bar');

const DEFAULT_BAR_CHART_HEIGHT = 219;
const DEFAULT_BAR_CHART_WIDTH = '100%';

export function createIncompleteAnnotationRenderer({data, dataKey, portalNode = document.body, text = t('incompleteAnnotation.text')}) {
    return function IncompleteAnnotation(props) {
        const {x, y, index, offset, width} = props;

        const {setTriggerRef, tooltipNode} = useRIQTooltip({
            text,
            placement: 'top',
            portalNode,
            delayShow: 200
        });

        if (data[index][dataKey] !== true) {
            return null;
        }

        return (
            <>
                <text
                    className='tlw-cursor-default tlw-select-none'
                    x={x + width / 2}
                    y={y - offset}
                    dominantBaseline='middle'
                    fill='#4c535e'
                    ref={setTriggerRef}
                    textAnchor='middle'
                >
                    *
                </text>
                {createPortal(tooltipNode, portalNode)}
            </>
        );
    };
}

export function Bar({barDataKey = 1, data, fill = '#1f77b4', height = DEFAULT_BAR_CHART_HEIGHT, label, tooltip, width = DEFAULT_BAR_CHART_WIDTH, xAxisDataKey = 0, xAxisTickFormatter = _.identity, yAxisAllowDecimals = true, yAxisTickFormatter = _.identity}) {

    const isExporting = Boolean(useContext(ExportContext));

    const [point, setPoint] = useState({
        idx: -1
    });

    const onMouseEnter = useCallback((data, idx, e) => {
        if (idx !== point?.idx) {
            setPoint(() => ({
                idx,
                payload: data.tooltipPayload,
                position: data.tooltipPosition
            }));
        }
    }, [point]);

    const onMouseLeave = useCallback((data, idx, e) => {
        setPoint(() => null);
    }, []);

    return (
        <ResponsiveContainer
            height={height}
            width={width}>
            <BarChart
                data={data}
            >
                <CartesianGrid
                    vertical={false}
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                />
                <XAxis
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                    dataKey={xAxisDataKey}
                    interval='preserveStartEnd'
                    tick={<XAxisTick indent={false} />}
                    tickFormatter={(tick) => xAxisTickFormatter(data[tick][xAxisDataKey])}
                />
                <YAxis
                    allowDecimals={yAxisAllowDecimals}
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                    tick={{
                        fill: '#4c535e',
                        fontSize: '12px'
                    }}
                    tickCount={6}
                    tickFormatter={yAxisTickFormatter}
                />
                <ChartTooltip
                    // this key will force a re-render on active change, which gives the tooltip lib a chance to remeasure. See #34320
                    key={`tooltip-${point?.idx}`}
                    tooltipPayload={point?.payload}
                    tooltipPoint={point?.position}
                    tooltipContent={tooltip}
                />
                <BarRecharts
                    dataKey={barDataKey}
                    fill={fill}
                    label={label}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    isAnimationActive={!isExporting}
                />
            </BarChart>
        </ResponsiveContainer>
    );
}

Bar.propTypes = {
    barDataKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.array.isRequired,
    fill: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    tooltip: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    xAxisDataKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    xAxisTickFormatter: PropTypes.func,
    yAxisTickFormatter: PropTypes.func
};

// colors are tricky
// trying to follow the model set out by the original stacked bar
// for now the colors are random, but we'll need to be able to pass in a specific color or palette eventually
// public/modules/graph/stackedBar.js - line 50
// public/modules/models/metric.js - line 360
// updates will still be needed see https://github.com/RivalIQ/rivaliq-server/pull/37996#discussion_r1607037469
function getColor(data) {
    const keysArr = Object.keys(data[0]).slice(1);
    const colors = [];
    keysArr.forEach((item, index) => {
        colors.push(d3.scale.category10().domain(d3.range(0, 10))(index % 10));
    });

    return colors;
}

export function StackedBar({data, dataKeys = [1, 2, 3], colors = getColor(data), height = DEFAULT_BAR_CHART_HEIGHT, label, tooltip, width = DEFAULT_BAR_CHART_WIDTH, xAxisDataKey = 0, xAxisTickFormatter = _.identity, yAxisTickFormatter = _.identity}) {

    const isExporting = Boolean(useContext(ExportContext));

    const [point, setPoint] = useState({
        idx: -1
    });

    const onMouseEnter = useCallback((data, idx, e) => {
        if (idx !== point?.idx) {
            setPoint(() => ({
                idx,
                payload: data.tooltipPayload,
                position: data.tooltipPosition
            }));
        }
    }, [point]);

    const onMouseLeave = useCallback((data, idx, e) => {
        setPoint(() => null);
    }, []);

    const bars = dataKeys.map((dataKey, index) => (
        <BarRecharts
            key={`bar-${index}`}
            dataKey={dataKey}
            stackId={1}
            fill={colors[index]}
            label={label}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            isAnimationActive={!isExporting}
        />
    ));

    return (
        <ResponsiveContainer
            height={height}
            width={width}>
            <BarChart
                data={data}
            >
                <CartesianGrid
                    vertical={false}
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                />
                <XAxis
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                    dataKey={xAxisDataKey}
                    interval='preserveStartEnd'
                    tick={<XAxisTick indent={false} />}
                    tickFormatter={(tick) => xAxisTickFormatter(data[tick][xAxisDataKey])}
                />
                <YAxis
                    stroke='#d3d3d3'
                    fill='none'
                    shapeRendering='crispEdges'
                    tick={{
                        fill: '#4c535e',
                        fontSize: '12px'
                    }}
                    tickCount={6}
                    tickFormatter={yAxisTickFormatter}
                />
                <ChartTooltip
                    // this key will force a re-render on active change, which gives the tooltip lib a chance to remeasure. See #34320
                    key={`tooltip-${point?.idx}`}
                    tooltipPayload={point?.payload}
                    tooltipPoint={point?.position}
                    tooltipContent={tooltip}
                />
                {bars}
            </BarChart>
        </ResponsiveContainer>
    );
}

StackedBar.propTypes = {
    data: PropTypes.array.isRequired,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    tooltip: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    xAxisDataKey: PropTypes.string,
    xAxisTickFormatter: PropTypes.func,
    yAxisTickFormatter: PropTypes.func
};
